<script setup>
// Vue
import { computed, onMounted, watch, ref } from 'vue';
import Timeline from 'primevue/timeline';
import Card from 'primevue/card';
import Paginator from 'primevue/paginator';
import ScrollPanel from 'primevue/scrollpanel';
import { useEmployeeStore } from '@/stores/employee.js';
import { fancyDate } from "@/filters/fancyDate.js";

const props = defineProps({
  // Объект и target для ленты событий.
  entityId: { type: [String, Number], required: true },
  target: { type: String, default: 'user' }, // work | facility | customer | contract | user
  showUser: { type: Boolean, default: false },
});

const employeeStore = useEmployeeStore();

// Текущая страница серверной пагинации
const currentPage = ref(1);

// Текущая полученная страница с бэка
const eventsPage = computed(() => employeeStore.events);
const events = computed(() => eventsPage.value.results || []);
const totalRecords = computed(() => eventsPage.value.count || 0);

// Кол-во элементов на странице: берём фактическое из results
const rows = computed(() => (events.value?.length || 25)); // дефолт если пусто

const loadPage = async (page) => {
  currentPage.value = page;
  await employeeStore.loadEvents({ target: props.target, id: props.entityId, page });
};

onMounted(() => {
  loadPage(1);
});

watch(() => [props.entityId, props.target], () => {
  loadPage(1);
});

const onPage = (e) => {
  // Paginator отдаёт 0-based page; сервер ожидает 1-based
  loadPage(e.page + 1);
};

// Справочник для типа цели
const targetTypeMap = {
  work: 'Работа',
  contract: 'Договор',
  facility: 'Объект',
  // Дополнительно (если встретится):
  customer: 'Клиент',
  user: 'пользователь',
};
const humanTargetType = (type) => targetTypeMap[type] || type || '—';

// Форматирование: дата через fancyDate, время — 24h без секунд
const formatDateTime = (iso) => {
  if (!iso) return '—';
  // fancyDate сам подменяет пробел на 'T'
  const dateText = fancyDate(iso);
  let timeText = '';
  try {
    const d = new Date(iso);
    timeText = d.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' });
  } catch {
  }
  return `${dateText} ${timeText}`.trim();
};
</script>

<template>
  <Card>
    <template #content>
      <div class="flex-1 min-h-0">
        <ScrollPanel style="width:100%;height:100%;">
          <Timeline :value="events" align="alternate" class="w-full h-100">
            <template #content="slotProps">
              <div class="event flex py-2 px-3 border-b items-start">
                <div class="flex-1 w-full">
                  <div class="flex items-center justify-between text-xs text-gray-500">
                    <span>{{ formatDateTime(slotProps.item?.created_at) }}</span>
                    <!-- В ленте сотрудника (target === 'user') ФИО актера не показываем -->
                    <span v-if="showUser && target !== 'user' && slotProps.item?.actor" class="italic">
                      {{ slotProps.item.actor }}
                    </span>
                  </div>

                  <div class="mt-1 text-sm space-y-1">
                    <div class="font-medium">
                      {{ slotProps.item?.verb || 'Событие' }}
                    </div>
                    <div v-if="slotProps.item?.target" class="text-gray-600">
                      <span class="ml-0">
                        {{ humanTargetType(slotProps.item.target.type) }} — {{ slotProps.item.target.name }}
                      </span>
                    </div>

<!--                    <div v-if="slotProps.item?.summary" class="text-gray-800">-->
<!--                      {{ slotProps.item.summary }}-->
<!--                    </div>-->
                  </div>
                </div>
              </div>
            </template>
          </Timeline>
        </ScrollPanel>
      </div>

      <Paginator
          :rows="rows"
          :totalRecords="totalRecords"
          :first="(currentPage - 1) * rows"
          :page="currentPage - 1"
          :rowsPerPageOptions="[rows]"
          @page="onPage"
          template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="Показано {first}–{last} из {totalRecords}"
      />
    </template>
  </Card>
</template>

<style scoped>
:deep(.p-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.p-card-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

:deep(.p-card-content) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
}
:deep(.p-timeline-event:last-child .p-timeline-event-marker) {
  margin-bottom: 2rem;
}
:deep(.p-timeline-event:last-child .p-timeline-event-content) {
  padding-bottom: 2rem;
}
</style>